import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, 
  Settings, 
  Trash2, 
  Package, 
  Factory, 
  AlertTriangle,
  TrendingUp,
  BarChart3,
  RefreshCw,
  GripVertical,
  X,
  Boxes,
  Clock,
  Users,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { openBrandedPrintWindow, PrintHelpers as H } from '../utils/brandedPrint';

// Widget Components
const InventorySummaryWidget = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-3 gap-4 text-center">
      <div>
        <p className="text-2xl font-bold text-slate-900">{data?.total_items || 0}</p>
        <p className="text-xs text-slate-500">Total Items</p>
      </div>
      <div>
        <p className="text-2xl font-bold text-green-600">${(data?.total_value || 0).toLocaleString()}</p>
        <p className="text-xs text-slate-500">Total Value</p>
      </div>
      <div>
        <p className="text-2xl font-bold text-red-600">{data?.low_stock_count || 0}</p>
        <p className="text-xs text-slate-500">Low Stock</p>
      </div>
    </div>
    {data?.by_category && Object.keys(data.by_category).length > 0 && (
      <div className="pt-2 border-t">
        <p className="text-xs font-semibold text-slate-500 mb-2">BY CATEGORY</p>
        <div className="space-y-1">
          {Object.entries(data.by_category).slice(0, 5).map(([cat, info]) => (
            <div key={cat} className="flex justify-between text-sm">
              <span className="text-slate-600">{cat}</span>
              <span className="font-medium">{info.count} items</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const LowStockWidget = ({ data }) => (
  <div className="space-y-2">
    <p className="text-sm text-slate-500">{data?.count || 0} items below reorder point</p>
    <div className="max-h-48 overflow-y-auto space-y-2">
      {data?.items?.slice(0, 8).map((item, idx) => (
        <div key={idx} className="flex justify-between items-center p-2 bg-red-50 rounded-sm">
          <div>
            <p className="font-medium text-sm">{item.sku}</p>
            <p className="text-xs text-slate-500">{item.name}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-red-600">{item.current_qty}</p>
            <p className="text-xs text-slate-400">of {item.reorder_point}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ProductionOverviewWidget = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4 text-center">
      <div>
        <p className="text-2xl font-bold text-slate-900">{data?.total_orders || 0}</p>
        <p className="text-xs text-slate-500">Work Orders</p>
      </div>
      <div>
        <p className="text-2xl font-bold text-blue-600">{data?.total_labor_hours?.toFixed(1) || 0}h</p>
        <p className="text-xs text-slate-500">Labor Hours</p>
      </div>
    </div>
    {data?.by_status && (
      <div className="pt-2 border-t">
        <p className="text-xs font-semibold text-slate-500 mb-2">BY STATUS</p>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(data.by_status).map(([status, count]) => (
            <div key={status} className="flex justify-between text-sm">
              <span className="text-slate-600">{status}</span>
              <span className="font-medium">{count}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const WasteAnalysisWidget = ({ data }) => (
  <div className="space-y-2">
    <p className="text-sm text-slate-500">Waste by reason</p>
    <div className="space-y-2">
      {data?.waste_by_reason && Object.entries(data.waste_by_reason).map(([reason, qty]) => (
        <div key={reason} className="flex justify-between items-center">
          <span className="text-sm text-slate-600">{reason}</span>
          <span className="font-medium text-sm text-red-600">{qty} units</span>
        </div>
      ))}
      {(!data?.waste_by_reason || Object.keys(data.waste_by_reason).length === 0) && (
        <p className="text-sm text-slate-400 text-center py-4">No waste recorded</p>
      )}
    </div>
  </div>
);

const LaborCostsWidget = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4 text-center">
      <div>
        <p className="text-2xl font-bold text-slate-900">{data?.total_labor_hours?.toFixed(1) || 0}h</p>
        <p className="text-xs text-slate-500">Total Hours</p>
      </div>
      <div>
        <p className="text-2xl font-bold text-green-600">${(data?.total_labor_cost || 0).toLocaleString()}</p>
        <p className="text-xs text-slate-500">Total Cost</p>
      </div>
    </div>
  </div>
);

const TransactionsWidget = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-3 gap-2 text-center">
      <div>
        <p className="text-lg font-bold text-slate-900">{data?.total_transactions || 0}</p>
        <p className="text-xs text-slate-500">Total</p>
      </div>
      <div>
        <p className="text-lg font-bold text-green-600">+{data?.total_in || 0}</p>
        <p className="text-xs text-slate-500">In</p>
      </div>
      <div>
        <p className="text-lg font-bold text-red-600">-{data?.total_out || 0}</p>
        <p className="text-xs text-slate-500">Out</p>
      </div>
    </div>
    {data?.recent?.length > 0 && (
      <div className="pt-2 border-t max-h-32 overflow-y-auto">
        <p className="text-xs font-semibold text-slate-500 mb-2">RECENT</p>
        {data.recent.slice(0, 5).map((tx, idx) => (
          <div key={idx} className="flex justify-between text-xs py-1">
            <span className="text-slate-600">{tx.item_sku}</span>
            <span className={tx.qty_change > 0 ? 'text-green-600' : 'text-red-600'}>
              {tx.qty_change > 0 ? '+' : ''}{tx.qty_change}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Widget icon mapping
const WIDGET_ICONS = {
  inventory_summary: Boxes,
  low_stock_alerts: AlertTriangle,
  stock_turnover: TrendingUp,
  production_overview: Factory,
  waste_analysis: Trash2,
  labor_costs: Clock,
  recent_transactions: BarChart3
};

const CustomDashboardPage = () => {
  const { api } = useAuth();
  const [widgets, setWidgets] = useState([]);
  const [availableWidgets, setAvailableWidgets] = useState({});
  const [widgetData, setWidgetData] = useState({});
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedWidgetType, setSelectedWidgetType] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [widgetsRes, availableRes] = await Promise.all([
        api().get('/dashboard/widgets'),
        api().get('/dashboard/available-widgets')
      ]);
      setWidgets(widgetsRes.data);
      setAvailableWidgets(availableRes.data);
      
      // Fetch data for each widget
      await fetchAllWidgetData(widgetsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllWidgetData = async (widgetsList) => {
    const dataPromises = widgetsList.map(widget => fetchWidgetData(widget.widget_type));
    const results = await Promise.all(dataPromises);
    
    const newData = {};
    widgetsList.forEach((widget, idx) => {
      newData[widget.widget_type] = results[idx];
    });
    setWidgetData(newData);
  };

  const fetchWidgetData = async (widgetType) => {
    try {
      let endpoint = '';
      switch (widgetType) {
        case 'inventory_summary':
        case 'stock_turnover':
          endpoint = '/reports/inventory-summary';
          break;
        case 'low_stock_alerts':
          endpoint = '/reports/low-stock';
          break;
        case 'production_overview':
        case 'waste_analysis':
        case 'labor_costs':
          endpoint = '/reports/production-summary';
          break;
        case 'recent_transactions':
          endpoint = '/reports/transactions-summary?days=7';
          break;
        default:
          return null;
      }
      
      const res = await api().get(endpoint);
      return res.data;
    } catch (error) {
      console.error(`Failed to fetch data for ${widgetType}`);
      return null;
    }
  };

  const handleAddWidget = async () => {
    if (!selectedWidgetType) return;
    
    try {
      const res = await api().post('/dashboard/widgets', {
        widget_type: selectedWidgetType
      });
      
      setWidgets([...widgets, res.data]);
      
      // Fetch data for new widget
      const data = await fetchWidgetData(selectedWidgetType);
      setWidgetData({ ...widgetData, [selectedWidgetType]: data });
      
      setAddDialogOpen(false);
      setSelectedWidgetType('');
      toast.success('Widget added');
    } catch (error) {
      toast.error('Failed to add widget');
    }
  };

  const handleRemoveWidget = async (widgetId) => {
    try {
      await api().delete(`/dashboard/widgets/${widgetId}`);
      setWidgets(widgets.filter(w => w.id !== widgetId));
      toast.success('Widget removed');
    } catch (error) {
      toast.error('Failed to remove widget');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllWidgetData(widgets);
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  // iter68 — branded printable analytics dashboard (renders each widget's data as a table)
  const printAnalytics = async () => {
    const sections = widgets.map(w => {
      const d = widgetData[w.widget_type];
      if (!d) return '';
      let body = '';
      if (Array.isArray(d)) {
        if (d.length === 0) { body = '<div class="value">No data.</div>'; }
        else {
          const keys = Object.keys(d[0]).filter(k => typeof d[0][k] !== 'object').slice(0, 8);
          body = H.table(keys.map(k => ({ key: k, label: k.replace(/_/g, ' ') })), d.slice(0, 100));
        }
      } else if (typeof d === 'object') {
        const rows = Object.entries(d)
          .filter(([, v]) => typeof v !== 'object')
          .map(([k, v]) => [k.replace(/_/g, ' '), String(v ?? '—')]);
        body = `<div class="grid-2">${rows.map(([k, v]) => `
          <div><div class="label">${H.escape(k)}</div><div class="value">${H.escape(v)}</div></div>
        `).join('')}</div>`;
      } else {
        body = `<div class="value">${H.escape(String(d))}</div>`;
      }
      return `${H.sectionTitle(w.title || w.widget_type)}${body}`;
    }).join('');
    await openBrandedPrintWindow({
      title: 'ANALYTICS DASHBOARD',
      subtitle: `${widgets.length} widget(s) · snapshot ${new Date().toLocaleString()}`,
      orientation: 'portrait',
      body: sections || H.callout('No widgets configured.'),
      footerNote: 'Internal Document · Analytics Snapshot',
    });
  };

  const renderWidgetContent = (widget) => {
    const data = widgetData[widget.widget_type];
    
    switch (widget.widget_type) {
      case 'inventory_summary':
      case 'stock_turnover':
        return <InventorySummaryWidget data={data} />;
      case 'low_stock_alerts':
        return <LowStockWidget data={data} />;
      case 'production_overview':
        return <ProductionOverviewWidget data={data} />;
      case 'waste_analysis':
        return <WasteAnalysisWidget data={data} />;
      case 'labor_costs':
        return <LaborCostsWidget data={data} />;
      case 'recent_transactions':
        return <TransactionsWidget data={data} />;
      default:
        return <p className="text-slate-500 text-sm">Widget not configured</p>;
    }
  };

  const getSizeClass = (size) => {
    switch (size) {
      case 'small': return 'col-span-1';
      case 'large': return 'col-span-2';
      default: return 'col-span-1';
    }
  };

  // Get widgets not yet added
  const unaddedWidgets = Object.entries(availableWidgets).filter(
    ([type]) => !widgets.some(w => w.widget_type === type)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="custom-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
          <p className="text-slate-500">Customizable metrics and reports</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={printAnalytics}
            className="rounded-sm"
            data-testid="analytics-print-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-orange-500 hover:bg-orange-600" data-testid="add-widget-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Widget
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Dashboard Widget</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Select value={selectedWidgetType} onValueChange={setSelectedWidgetType}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select widget type" />
                  </SelectTrigger>
                  <SelectContent>
                    {unaddedWidgets.map(([type, info]) => (
                      <SelectItem key={type} value={type}>
                        <div className="flex items-center gap-2">
                          <span>{info.title}</span>
                          <span className="text-xs text-slate-400">({info.category})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedWidgetType && availableWidgets[selectedWidgetType] && (
                  <p className="text-sm text-slate-500">
                    {availableWidgets[selectedWidgetType].description}
                  </p>
                )}
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={handleAddWidget}
                    disabled={!selectedWidgetType}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    Add Widget
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Widgets Grid */}
      {widgets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {widgets.filter(w => w.is_visible).map((widget) => {
            const IconComponent = WIDGET_ICONS[widget.widget_type] || BarChart3;
            
            return (
              <Card 
                key={widget.id} 
                className={`industrial-card ${getSizeClass(widget.size)}`}
                data-testid={`widget-${widget.widget_type}`}
              >
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IconComponent className="w-5 h-5 text-orange-500" />
                    <CardTitle className="text-base">{widget.title}</CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveWidget(widget.id)}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {renderWidgetContent(widget)}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="industrial-card">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">No widgets configured</p>
            <Button 
              onClick={() => setAddDialogOpen(true)}
              className="rounded-sm bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Widget
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Available Widgets Info */}
      <Card className="industrial-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Available Widgets
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['inventory', 'financial', 'production'].map(category => (
              <div key={category}>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">{category}</p>
                <div className="space-y-1">
                  {Object.entries(availableWidgets)
                    .filter(([, info]) => info.category === category)
                    .map(([type, info]) => {
                      const isAdded = widgets.some(w => w.widget_type === type);
                      return (
                        <div key={type} className="flex items-center justify-between text-sm">
                          <span className={isAdded ? 'text-slate-400' : 'text-slate-600'}>
                            {info.title}
                          </span>
                          {isAdded && (
                            <span className="text-xs text-green-600">Added</span>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomDashboardPage;
